import {
  AuthFill,
  Button,
  Comments,
  Date,
  Email,
  FindDealer,
  FirstName,
  LastName,
  MarketingConsent,
  Models,
  Tel,
  Title,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { useCampaign, useDealer } from '@contexts/index'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import { closeOverlay } from '@utils/helpers/index-client'
import { getContentFile, getModels, getPageData } from '@utils/helpers/index-server'
import { formEvent, updateDatalayer } from '@utils/helpers/tracking'
import { VideoConsultationSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { upperCaseFirst } from '@utils/helpers/stringHelpers'

const VideoConsultation = ({
  models,
  content,
  tracking,
  brand,
  dealer: serverDealer,
  model,
  query,
}) => {
  const campaign = useCampaign()
  const clientDealer = useDealer()
  const dealer = clientDealer || serverDealer
  const [isDealer, setIsDealer] = useState(false)

  const availability = (date) => date.getDay() !== 0 // exclude sunday

  const [submitResponse, setSubmitResponse] = useState('')

  const initialValues = {
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    tel: '',
    model: '',
    date: '',
    time: '',
    consent: {
      user: false,
    },
    response_code: '',
    campaign_response_code: '',
    campaign_code: '',
    crm_response_source: '',
    is_dealer: query?.source === 'dealer' || query?.source === 'centre',
    dealer: '',
    dealer_name: '',
    notes: '',
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(VideoConsultationSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  useEffect(() => {
    if (campaign?.campaign) {
      methods.setValue('campaign_code', campaign.campaign)
    }

    if (campaign?.media) {
      const code = methods.getValues('campaign_code')
      methods.setValue('campaign_code', code + campaign.media)
    }

    if (campaign?.utm) {
      methods.setValue('crm_response_source', `Web ${campaign.utm}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign])

  useEffect(() => {
    if (dealer) {
      methods.setValue(
        'dealer',
        dealer?.applications?.NMSC?.externalID
          ? dealer.applications.NMSC.externalID
          : '',
      )
      methods.setValue('dealer_name', dealer.name)
      methods.setValue('is_dealer', true)
      setIsDealer(true)
      updateDatalayer('dealer', {
        uuid: dealer.uuid,
        name: dealer.name,
        address: {
          city: dealer.addresses[0].city,
          zip: dealer.addresses[0].zip,
        },
      })
    }
  }, [dealer])

  useEffect(() => {
    if (model) {
      methods.setValue('model', model.d4t4.id)
      methods.trigger('model')
    }
  }, [model])

  const onSubmit = async (data) => {
    const formData = data
    formData.response_code = brand === 'lexus' ? 'RC116737' : 'RC120152'

    if (formData.is_dealer) {
      formData.response_code = brand === 'lexus' ? 'RC116737' : 'RC120153'
    }

    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/video-consultation`,
      headers: {
        'Content-Type': 'application/json',
      },
      data: formData,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          formData.email,
        )
        closeOverlay(
          content.confirmation.title,
          content?.confirmation?.copy || '',
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep subtitle={content.details.subtitle} active={true}>
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              <AuthFill />
              <Models models={models} />
              <div className="h-8"></div>
              {!isDealer ? <FindDealer /> : ''}
              <Date
                availability={availability}
                showTime={true}
                helper="Let us know if you have a preferred date or time and we'll do our best to arrange your booking for this time."
              />
              <div className="h-8"></div>
              <Title optional />
              <FirstName />
              <LastName />
              <Email />
              <Tel
                optional
                helper="We will use your phone number to discuss your consultation over the phone."
              />
              <Comments
                optional
                label="Additional Comments"
                helper="Let us know if you have any specific requirements."
                characterLimit={brand === 'lexus' ? 140 : 255}
              />
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'video-consultation'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }
  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }
  return {
    props: {
      models: getModels(brand, 'video-consulation'),
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'contact-details',
      },
      description: `${upperCaseFirst(brand)} copy missing`,
      ...(await getPageData(context, form)),
    },
  }
}

export default VideoConsultation
